import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { groupBy } from 'lodash';
import { selectIsDemoMode, selectDemoShowings } from './demo';

// Base showings selector
const selectBaseShowings = (state: RootState) => state.showings.showings;

// Enhanced selector that includes demo data when in demo mode
export const selectShowings = createSelector(
  [selectBaseShowings, selectIsDemoMode, selectDemoShowings],
  (baseShowings, isDemoMode, demoShowings) => {
    if (!isDemoMode) return baseShowings;
    // Put demo showings first
    return [...demoShowings, ...baseShowings];
  }
);

export const selectSellingShowings = createSelector(
  [selectShowings],
  (showings) => {
    return showings
      .filter((showing) => showing.listings?.type === 'SELLER')
      .sort(
        (a, b) =>
          new Date(b.proposedTime).getTime() -
          new Date(a.proposedTime).getTime()
      );
  }
);

// Cancellation contract, matching ShowingTime.tsx.
const isCancelledShowing = (showing: any) =>
  showing.accepted === null || !!showing.cancelledAt;

export const selectShowingsGroupedByBuyer = createSelector(
  [selectSellingShowings],
  (showings) => {
    // The Buyers table renders only showings[0] per buyer. Order active
    // showings ahead of cancelled ones (preserving proposedTime desc within
    // each group) so an active request always wins, but a buyer whose only
    // showing is cancelled still shows "Cancelled" instead of "None". Without
    // this, a reschedule to an earlier time would sort the cancelled original
    // ahead of the live replacement and hide it from the seller.
    const ordered = [...showings].sort((a, b) => {
      const aCancelled = isCancelledShowing(a) ? 1 : 0;
      const bCancelled = isCancelledShowing(b) ? 1 : 0;
      if (aCancelled !== bCancelled) return aCancelled - bCancelled;
      return (
        new Date(b.proposedTime).getTime() - new Date(a.proposedTime).getTime()
      );
    });
    return groupBy(ordered, 'userId');
  }
);

export const selectBuyingShowings = createSelector(
  [selectShowings],
  (showings) => {
    return showings
      .filter((showing) => showing.listings?.type === 'BUYER')
      .sort(
        (a, b) =>
          new Date(b.proposedTime).getTime() -
          new Date(a.proposedTime).getTime()
      );
  }
);

// Select upcoming showings (future dates only, excluding cancelled).
// Matches the cancellation contract in ShowingTime.tsx: cancelled = accepted===null || cancelledAt set.
const isUpcomingActive = (showing: any, now: Date) =>
  !showing.cancelledAt &&
  showing.accepted !== null &&
  new Date(showing.proposedTime) > now;

export const selectUpcomingSellingShowings = createSelector(
  [selectSellingShowings],
  (showings) => {
    const now = new Date();
    return showings.filter((s) => isUpcomingActive(s, now));
  }
);

export const selectUpcomingBuyingShowings = createSelector(
  [selectBuyingShowings],
  (showings) => {
    const now = new Date();
    return showings.filter((s) => isUpcomingActive(s, now));
  }
);

// User-context-aware selector for showing by listingId and userId (works for all user types)
export const selectShowingByListingIdAndUserId = (
  listingId: string,
  userId: string
) =>
  createSelector([selectShowings], (showings) =>
    showings.find(
      (showing) => showing.listingId === listingId && showing.userId === userId
    )
  );

// Returns ALL showings for a user+listing pair, sorted by proposedTime descending (newest first)
export const selectShowingsByListingIdAndUserId = (
  listingId: string,
  userId: string
) =>
  createSelector([selectShowings], (showings) =>
    showings
      .filter(
        (showing) => showing.listingId === listingId && showing.userId === userId
      )
      .sort(
        (a, b) =>
          new Date(b.proposedTime).getTime() -
          new Date(a.proposedTime).getTime()
      )
  );

// Find a showing by the linked MLS listing ID (checks buyer listing's mlsListingId)
export const selectShowingByMlsListingId = (mlsListingId: string) =>
  createSelector([selectShowings], (showings) =>
    showings.find(
      (showing) => showing.listings?.mlsListingId === mlsListingId
    )
  );
